import { defineStore } from 'pinia';
import { useOrgStore } from '@/stores/org';

/**
 * Per-organization default column configuration for object lists (e.g. tasks).
 * Mirrors the objectConfig store: cached per object type, invalidated after an
 * admin saves changes in the Lists settings.
 */
export const useListConfigStore = defineStore('listConfig', {
    state: () => ({
        configs: {},
        loaded: {},
        loading: {}
    }),

    getters: {
        getConfig: state => objectType => state.configs[objectType] || null,
        isLoaded: state => objectType => !!state.loaded[objectType]
    },

    actions: {
        async loadConfig(objectType) {
            if (this.loaded[objectType]) {
                return this.configs[objectType];
            }
            if (this.loading[objectType]) {
                // Wait for the in-flight request by polling
                await new Promise(resolve => {
                    const interval = setInterval(() => {
                        if (!this.loading[objectType]) {
                            clearInterval(interval);
                            resolve();
                        }
                    }, 50);
                });
                return this.configs[objectType];
            }

            this.loading[objectType] = true;
            try {
                const orgStore = useOrgStore();
                const api = orgStore.api;
                const { data } = await axios.get(`${api}/list-config/${objectType}`);
                // Answered for an organization since left: not this one's columns.
                if (orgStore.api !== api) return null;
                this.configs[objectType] = data;
                this.loaded[objectType] = true;
            } catch {
                // Non-critical: lists fall back to their built-in default columns
            } finally {
                this.loading[objectType] = false;
            }

            return this.configs[objectType] || null;
        },

        /**
         * Take an answer already on its way — the list bootstrap's. Callers meanwhile
         * wait on `loading` as for any in-flight request; a part the bootstrap left
         * out (null) falls back to the endpoint.
         *
         * @param {string} objectType
         * @param {Promise<Array|null>} pending
         */
        prime(objectType, pending) {
            if (this.loaded[objectType] || this.loading[objectType]) {
                return;
            }

            this.loading[objectType] = true;
            pending.then(
                data => {
                    this.loading[objectType] = false;
                    if (data == null) {
                        return this.loadConfig(objectType);
                    }
                    this.configs[objectType] = data;
                    this.loaded[objectType] = true;
                },
                () => {
                    this.loading[objectType] = false;
                    return this.loadConfig(objectType);
                }
            );
        },

        invalidate(objectType) {
            delete this.loaded[objectType];
            delete this.configs[objectType];
        },

        invalidateAll() {
            this.configs = {};
            this.loaded = {};
            this.loading = {};
        }
    }
});
